import { useCallback, useEffect, useRef, useState } from "react";
import { supabase, safeChannel } from "./supabase";
import { compressImage } from "./compressImage";

// useRecipePhotos — every photo associated with a single recipe
// slug, sourced from BOTH recipe_photos (manual header uploads,
// migration 0185) and cook_log_photos (auto from finished cooks,
// migration 0018). Sorted recency-desc, deduped by storage_path.
//
// Returns:
//   { photos: [{url, source, uploadedAt, id, storage_path}],
//     primary: photo | null,
//     loading,
//     uploadFromFile(file),
//     remove(photo) }
//
// Family-scoped via RLS on both tables. No localStorage cache —
// recipe photos are small in number per recipe and realtime keeps
// the list fresh.

const BUCKET = "meal-photos";

function urlFor(path) {
  const { data } = supabase.storage.from(BUCKET).getPublicUrl(path);
  return data?.publicUrl || null;
}

export function useRecipePhotos(recipeSlug, userId) {
  const [recipeRows, setRecipeRows] = useState([]);
  const [cookRows, setCookRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState(null);
  const chRef = useRef(null);

  const load = useCallback(async () => {
    if (!recipeSlug) {
      setRecipeRows([]);
      setCookRows([]);
      setLoading(false);
      return;
    }
    setLoading(true);

    const recipeQ = supabase
      .from("recipe_photos")
      .select("id, storage_path, uploader_id, is_primary, created_at")
      .eq("recipe_slug", recipeSlug)
      .order("created_at", { ascending: false });

    const cookQ = supabase
      .from("cook_log_photos")
      .select("id, storage_path, uploader_id, created_at, cook_logs!inner(recipe_slug)")
      .eq("cook_logs.recipe_slug", recipeSlug)
      .order("created_at", { ascending: false })
      .limit(50);

    const [recipeRes, cookRes] = await Promise.all([recipeQ, cookQ]);
    if (recipeRes.error) {
      console.warn("[recipe-photos] recipe_photos:", recipeRes.error.message);
    }
    if (cookRes.error) {
      console.warn("[recipe-photos] cook_log_photos:", cookRes.error.message);
    }
    setRecipeRows(recipeRes.data || []);
    setCookRows(cookRes.data || []);
    setLoading(false);
  }, [recipeSlug]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (!recipeSlug) return;
    const ch = safeChannel(`rt:recipe_photos:${recipeSlug}`)
      .on("postgres_changes",
        { event: "*", schema: "public", table: "recipe_photos", filter: `recipe_slug=eq.${recipeSlug}` },
        () => load())
      .on("postgres_changes",
        { event: "INSERT", schema: "public", table: "cook_log_photos" },
        () => load())
      .subscribe();
    chRef.current = ch;
    return () => {
      if (chRef.current) {
        supabase.removeChannel(chRef.current);
        chRef.current = null;
      }
    };
  }, [recipeSlug, load]);

  // Merge and sort: recipe_photos first by recency, then
  // cook_log_photos. Dedupe by storage_path. Primary recipe photos
  // bubble to the top so the user can curate the hero.
  const photos = (() => {
    const seen = new Set();
    const out = [];
    const primaryFirst = [
      ...recipeRows.filter((r) => r.is_primary),
      ...recipeRows.filter((r) => !r.is_primary),
    ];
    for (const r of primaryFirst) {
      if (seen.has(r.storage_path)) continue;
      seen.add(r.storage_path);
      out.push({
        id: r.id,
        source: "recipe",
        is_primary: !!r.is_primary,
        storage_path: r.storage_path,
        uploader_id: r.uploader_id,
        url: urlFor(r.storage_path),
        uploadedAt: r.created_at,
      });
    }
    for (const c of cookRows) {
      if (seen.has(c.storage_path)) continue;
      seen.add(c.storage_path);
      out.push({
        id: c.id,
        source: "cook",
        is_primary: false,
        storage_path: c.storage_path,
        uploader_id: c.uploader_id,
        url: urlFor(c.storage_path),
        uploadedAt: c.created_at,
      });
    }
    return out.filter((p) => p.url);
  })();

  const primary = photos[0] || null;

  // Upload helper. Compresses via the existing compressImage util,
  // pushes to meal-photos bucket under recipe/<slug>/<uuid>.jpg,
  // then inserts a recipe_photos row. Fire-and-forget realtime
  // refreshes the list.
  const uploadFromFile = useCallback(async (file, { setAsPrimary = false } = {}) => {
    if (!file || !userId || !recipeSlug) return null;
    setUploading(true);
    setError(null);
    try {
      const compressed = await compressImage(file, {
        maxDimension: 1600,
        jpegQuality: 0.82,
      });
      // compressImage returns { base64, mediaType, size } — NOT a
      // Blob. Pre-fix, this object was passed straight to
      // storage.upload() which silently coerced it to garbage
      // bytes; uploads "succeeded" but rendered as broken images.
      // Decode the base64 back into a proper Blob before uploading.
      const byteString = atob(compressed.base64 || "");
      const bytes = new Uint8Array(byteString.length);
      for (let i = 0; i < byteString.length; i++) {
        bytes[i] = byteString.charCodeAt(i);
      }
      const blob = new Blob([bytes], { type: compressed.mediaType || "image/jpeg" });
      const id = (crypto?.randomUUID?.() || `${Date.now()}-${Math.random().toString(36).slice(2)}`);
      const path = `recipe/${recipeSlug}/${id}.jpg`;
      const up = await supabase.storage.from(BUCKET).upload(path, blob, {
        contentType: "image/jpeg",
        upsert: false,
        cacheControl: "3600",
      });
      if (up.error) throw up.error;

      const ins = await supabase.from("recipe_photos").insert({
        recipe_slug:  recipeSlug,
        uploader_id:  userId,
        storage_path: path,
        is_primary:   setAsPrimary,
      });
      if (ins.error) throw ins.error;

      if (setAsPrimary) {
        await supabase.from("recipe_photos")
          .update({ is_primary: false })
          .eq("recipe_slug", recipeSlug)
          .eq("uploader_id", userId)
          .neq("storage_path", path);
      }

      await load();
      return urlFor(path);
    } catch (e) {
      console.error("[recipe-photos] upload:", e?.message || e);
      setError(e?.message || "upload failed");
      return null;
    } finally {
      setUploading(false);
    }
  }, [userId, recipeSlug, load]);

  const remove = useCallback(async (photo) => {
    if (!photo || photo.source !== "recipe") return;
    const { error } = await supabase.from("recipe_photos").delete().eq("id", photo.id);
    if (error) {
      console.warn("[recipe-photos] delete:", error.message);
      setError(error.message);
      return;
    }
    await supabase.storage.from(BUCKET).remove([photo.storage_path]).catch(() => {});
    await load();
  }, [load]);

  const setPrimary = useCallback(async (photo) => {
    if (!photo || photo.source !== "recipe" || photo.uploader_id !== userId) return;
    await supabase.from("recipe_photos")
      .update({ is_primary: false })
      .eq("recipe_slug", recipeSlug)
      .eq("uploader_id", userId);
    await supabase.from("recipe_photos")
      .update({ is_primary: true })
      .eq("id", photo.id);
    await load();
  }, [userId, recipeSlug, load]);

  return {
    photos,
    primary,
    loading,
    uploading,
    error,
    uploadFromFile,
    remove,
    setPrimary,
    refresh: load,
  };
}
